class LocalActiveSpan {
    _recorder;
    _name;
    _startedAt;
    _startOrder;
    _attributes = new Map();
    _counters = new Map();
    _events = [];
    _ended = false;
    constructor(recorder, name, startedAt, startOrder, attributes) {
        this._recorder = recorder;
        this._name = name;
        this._startedAt = startedAt;
        this._startOrder = startOrder;
        for (const [key, value] of Object.entries(attributes ?? {})) {
            this._attributes.set(key, value);
        }
    }
    setAttribute(name, value) {
        if (this._ended)
            return;
        this._attributes.set(name, value);
    }
    addEvent(name, attributes) {
        if (this._ended)
            return;
        this._events.push({ name, attributes: sortAttributes(attributes ?? {}) });
    }
    incrementCounter(name, delta = 1) {
        if (this._ended || !Number.isFinite(delta))
            return;
        this._counters.set(name, (this._counters.get(name) ?? 0) + delta);
    }
    end(error) {
        if (this._ended)
            return;
        this._ended = true;
        this._recorder._record({
            name: this._name,
            durationMs: Math.max(0, this._recorder.clock() - this._startedAt),
            attributes: mapToSortedRecord(this._attributes),
            counters: mapToSortedRecord(this._counters),
            events: [...this._events],
            error: error === undefined ? undefined : formatSpanError(error),
            startOrder: this._startOrder,
        });
    }
}
export class LocalInstrumentationRecorder {
    clock;
    _records = [];
    _nextStartOrder = 0;
    constructor(clock) {
        this.clock = clock;
    }
    run(name, fn, options) {
        const span = this.startSpan(name, options);
        try {
            const result = fn(span);
            span.end();
            return result;
        }
        catch (error) {
            span.end(error);
            throw error;
        }
    }
    async runAsync(name, fn, options) {
        const span = this.startSpan(name, options);
        try {
            const result = await fn(span);
            span.end();
            return result;
        }
        catch (error) {
            span.end(error);
            throw error;
        }
    }
    startSpan(name, options) {
        return new LocalActiveSpan(this, name, this.clock(), this._nextStartOrder++, options?.attributes);
    }
    records() {
        return this._records.map((record) => ({
            name: record.name,
            durationMs: record.durationMs,
            attributes: { ...record.attributes },
            counters: { ...record.counters },
            events: record.events.map((event) => ({ ...event, attributes: { ...event.attributes } })),
            error: record.error,
        }));
    }
    summary() {
        const aggregates = new Map();
        for (const record of this._records) {
            let aggregate = aggregates.get(record.name);
            if (!aggregate) {
                aggregate = {
                    name: record.name,
                    totalMs: 0,
                    calls: 0,
                    maxMs: 0,
                    firstStartOrder: record.startOrder,
                    attributes: new Map(),
                    counters: new Map(),
                    errors: 0,
                };
                aggregates.set(record.name, aggregate);
            }
            aggregate.totalMs += record.durationMs;
            aggregate.calls++;
            aggregate.maxMs = Math.max(aggregate.maxMs, record.durationMs);
            aggregate.firstStartOrder = Math.min(aggregate.firstStartOrder, record.startOrder);
            if (record.error !== undefined)
                aggregate.errors++;
            for (const [key, value] of Object.entries(record.attributes)) {
                const values = aggregate.attributes.get(key) ?? new Set();
                values.add(value);
                aggregate.attributes.set(key, values);
            }
            for (const [key, value] of Object.entries(record.counters)) {
                aggregate.counters.set(key, (aggregate.counters.get(key) ?? 0) + value);
            }
        }
        return [...aggregates.values()]
            .sort((a, b) => a.firstStartOrder - b.firstStartOrder)
            .map((aggregate) => {
            const attributes = summaryAttributes(aggregate.attributes);
            const counters = mapToSortedRecord(aggregate.counters);
            return {
                name: aggregate.name,
                totalMs: aggregate.totalMs,
                calls: aggregate.calls,
                averageMs: aggregate.calls === 0 ? 0 : aggregate.totalMs / aggregate.calls,
                maxMs: aggregate.maxMs,
                ...(Object.keys(attributes).length > 0 ? { attributes } : {}),
                ...(Object.keys(counters).length > 0 ? { counters } : {}),
                ...(aggregate.errors > 0 ? { errors: aggregate.errors } : {}),
            };
        });
    }
    _record(record) {
        this._records.push(record);
    }
}
function sortAttributes(attributes) {
    return Object.fromEntries(Object.entries(attributes).sort(([a], [b]) => a.localeCompare(b)));
}
function mapToSortedRecord(map) {
    return Object.fromEntries([...map.entries()].sort(([a], [b]) => a.localeCompare(b)));
}
function summaryAttributes(attributes) {
    return Object.fromEntries([...attributes.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, values]) => [
        key,
        [...values].sort((a, b) => String(a).localeCompare(String(b))),
    ]));
}
function formatSpanError(error) {
    if (error instanceof Error)
        return error.message;
    return typeof error === "string" ? error : String(error);
}
//# sourceMappingURL=local-recorder.js.map